import { AvailableLimitedAchievementPeriodsType } from "types/achievements";
import {
  AudienceType,
  CampaignFEValues,
  CampaignTriggers,
  CampaignTypes,
  SelectedLimitType,
  TransactionCampaigns,
  TriggerStrategyType,
} from "types/campaigns";
import { CustomEventSchema } from "types/customEvents";
import {
  prepareBECampaignLimitFormatForFE,
  prepareVisibilityData,
} from "../Common/campaignHelpers";
import { generateRules } from "../Common/CampaignRules/rulesHelpers";
import { itemFilters } from "../Referrals/ReferralsForm/CampaignTransactionItemFilters/helpers";

export const handleCampaignLimitUnit = (intervalType: string) => {
  switch (intervalType) {
    case "calendarHours":
      return "perHour";
    case "calendarDays":
      return "perDay";
    case "calendarWeeks":
      return "perWeek";
    case "calendarMonths":
      return "perMonth";
    case "calendarYears":
      return "perYear";
    default:
      return "";
  }
};

export const prepareIntervalType = (limitType?: SelectedLimitType) =>
  !limitType
    ? "unlimited"
    : limitType?.value && !limitType?.interval
      ? "inTotal"
      : handleCampaignLimitUnit(limitType?.interval?.type);

export const prepareInitialValues = ({
  campaignData,
  selectedSchemaDetails,
}: {
  campaignData: CampaignFEValues;
  selectedSchemaDetails?: CustomEventSchema;
}) => ({
  active: campaignData?.active,
  activity: {
    startsAt: campaignData.activity?.startsAt as string,
    endsAt: campaignData.activity?.endsAt || null,
  },
  labels: campaignData.labels || [],
  limits: prepareBECampaignLimitFormatForFE(campaignData.limits),
  rules: generateRules({
    rules: campaignData.rules,
    campaignTrigger: campaignData.trigger,
    campaignType: campaignData.type,
    selectedSchemaDetails,
  }),
  transactionItemsFilters:
    (campaignData as TransactionCampaigns)?.transactionItemsFilters?.map(
      ({ name, code, filters }) => ({
        name,
        code,
        filters: filters.map(({ value, condition }) => ({
          value,
          condition,
          type: itemFilters.find(({ id }) => value.field === id)?.type,
          label: itemFilters.find(({ id }) => value.field === id)?.label,
          isSaved: true,
          isEditMode: false,
        })),
      }),
    ) || [],
  displayOrder: campaignData.displayOrder,
  type:
    campaignData.trigger === CampaignTriggers.TIME
      ? CampaignTypes.TIME
      : campaignData.type,
  trigger: campaignData.trigger,
  ...(campaignData?.type === CampaignTypes.REFERRAL && {
    multiLevel: campaignData.multiLevel,
  }),
  ...(campaignData?.trigger === CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE && {
    eventCodeAttribute: campaignData.eventCodeAttribute,
    generateCodes: campaignData.generateCodes,
    codeGenerator: campaignData.codeGenerator,
  }),
  event: "event" in campaignData ? campaignData.event : undefined,
  ...(campaignData?.trigger === CampaignTriggers.ACHIEVEMENT && {
    achievementId: campaignData.achievementId,
  }),
  visibility:
    "visibility" in campaignData
      ? prepareVisibilityData(campaignData.visibility)
      : { target: "all" },
  ...("triggerStrategy" in campaignData &&
    campaignData?.triggerStrategy?.type && {
      audience: campaignData?.audience
        ? campaignData.audience
        : { target: AudienceType.EVERYONE },
    }),
  ...("triggerStrategy" in campaignData && {
    triggerStrategy:
      // Backwards compatibility for Birthday campaigns
      campaignData.trigger === CampaignTriggers.TIME &&
      !campaignData.triggerStrategy.type
        ? { type: TriggerStrategyType.BIRTHDAY }
        : campaignData.triggerStrategy,
  }),
});

export const limitsOptions: {
  value: AvailableLimitedAchievementPeriodsType;
  label: string;
}[] = [
  { label: "unlimited", value: "noLimit" },
  { label: "perHour", value: "calendarHours" },
  { label: "perDay", value: "calendarDays" },
  { label: "perWeek", value: "calendarWeeks" },
  { label: "perMonth", value: "calendarMonths" },
  { label: "perYear", value: "calendarYears" },
  { label: "inTotal", value: "inTotal" },
];
